<template>
  <vue-final-modal
    v-slot="{close}"
    :name="DEFAULT_MODAL_NAME"
    v-bind="$attrs"
    classes="modal-container"
    content-class="modal-content"
    :fit-parent="true"
    :esc-to-close="true"
  >
    <div
      :class="['igc-modal', severityClass, direction, classList]"
      @click="event => event.stopPropagation()"
    >
      <div
        v-if="showCloseButton"
        class="close-button"
        @click="() => close()"
      />
      <h2 class="container title">
        {{ t(title) }}
      </h2>

      <hr
        v-if="isMultipart"
        class="divider"
      >

      <div
        class="container content"
        v-html="translatedMessage"
      />

      <slot @onClose="onClose" />

      <component
        :is="component"
        v-if="component"
      />

      <div
        v-if="buttons"
        class="igc-modal__buttons"
      >
        <IgcButton
          v-for="(button, index) in buttons"
          :key="index"
          rounded
          :type="button.type"
          class="igc-modal__button"
          @click="button.handler(resolve, reject)"
        >
          {{ t(button.title) }}
        </IgcButton>
      </div>
    </div>
  </vue-final-modal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, defineAsyncComponent } from 'vue';

import { useI18n } from '@/shared/helpers/i18n';
import { Severity } from '@/shared/types';
import { storageService } from '@/shared/services';
import { DEFAULT_MODAL_NAME } from '@/shared/plugins/ModalPlugin/IgcModalService';

const IgcButton = defineAsyncComponent(() => import('../IgcButton/IgcButton.vue'));
const { t } = useI18n();

const props = defineProps({
  severity: {
    type: String as PropType<Severity>,
    required: false,
    default: '',
  },

  title: {
    type: String as PropType<string>,
    required: true,
  },

  message: {
    type: String as PropType<string>,
    default: '',
    required: false,
  },

  buttons: {
    type: Array as PropType<any[]>,
    required: false,
    default: () => [],
  },

  resolve: {
    type: Function as PropType<() => void>,
    required: false,
    default: () => (() => console.log('modal resolve is not defined')),
  },

  reject: {
    type: Function as PropType<() => void>,
    required: false,
    default: () => (() => console.log('modal reject is not defined')),
  },

  component: {
    type: [Array, Number, Object, String, Boolean] as PropType<any>,
    default: () => ({}),
    required: false,
  },

  // 'full-width'
  classList: {
    type: String as PropType<string>,
    default: '',
    required: false,
  },

  showCloseButton: {
    type: Boolean as PropType<boolean>,
    default: true,
    required: false,
  },
});

const emits = defineEmits(['close']);

const direction = computed(() => (storageService.session.get('lang') === 'he' ? 'rtl' : 'ltr'));
const isMultipart = computed(() => props.title && (props.message || props.component));
const severityClass = computed(() => (props.severity ? `severity-${props.severity}` : 'severity-default'));
const translatedMessage = computed(() => t(props.message));

function onClose() {
  emits('close');
}
</script>

<script lang="ts">
export default {
  name: 'IgcModal',
  inheritAttrs: false,
};
</script>

<style lang="less" scoped>
/* final-modal-recommended styles */
::v-deep(.modal-container) {
  display: flex;
  justify-content: center;
  align-items: center;
}
::v-deep(.modal-content) {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background: #fff;
}
.modal__title {
  margin: 0 2rem 0 0;
  font-size: 1.5rem;
  font-weight: 700;
}
.modal__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}

/* compatibility gist */
.modal {
  &-content {
    // default settings of vue-js-modal
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
    background-color: white;
    border-radius: 3px;
    box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.4);
    width: 600px;

    // step-to-step settings of vue-final-modal
    display: flex;
    flex-direction: column;
    //margin: 0 1rem;
    //padding: 1rem;
    //border: 1px solid #e2e8f0;
    //border-radius: 0.25rem;
    //background: #fff;
  }
  &-container {
    // step-to-step settings of vue-final-modal
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.igc-modal {
  --theme-local:      var(--primary--mario);
  --theme-text-local: var(--neutral--gabriel);
  --neutral-local:    var(--neutral--anne);

  &.severity-info {
    --theme-local: var(--secondary--eric);
  }

  &.severity-success {
    --theme-local: var(--accent--steve);
  }

  &.severity-error {
    --theme-local: var(--accent--spencer);
  }
}

/* stylelint-disable-next-line */
.igc-modal {
  max-width:   345px;
  padding:     38px 30px 30px 30px;
  display:     flex;
  flex-flow:   column;
  align-items: center;
  text-align:  center;
  box-sizing:  border-box;

  &.full-width {
    max-width:     385px;
    margin:        0;
    padding-left:  0;
    padding-right: 0;
  }

  &.learning-center-modal {
    max-width: 600px;
    padding: 30px 0 0 0;
  }
}

.close-button {
  background:      var(--theme-local);
  border-radius:   50%;
  width:           30px;
  height:          30px;
  cursor:          pointer;
  position:        absolute;
  top:             -15px;
  right:           15px;
  display:         flex;
  justify-content: center;
  align-items:     center;
  z-index:         25;

  &::after {
    content:   '\00d7'; /* the X */
    color:     var(--theme-text-local);
    display:   inline-block;
    font-size: 20px;
  }
}

::v-deep(.info-block) {
  color:       var(--theme-local);
  font-weight: 600;
  text-align:  start;
}

::v-deep(button.igc-btn) {
  background-color: var(--theme-local);
}

.container {
  width:         100%;
  overflow:      hidden;
  text-overflow: ellipsis;
}

.title {
  margin:         0;
  color:          var(--theme-local);
  font-size:      26px;
  font-weight:    600;
  letter-spacing: 0;
  line-height:    39px;
  text-align:     center;
}

hr.divider {
  border:    1px solid var(--neutral-local);
  width:     80%;
  max-width: 285px;
  margin:    20px 30px;
}

.igc-modal__buttons {
  margin-top: 60px;
}

.igc-modal__button {
  margin-bottom: 15px;
}

</style>
